import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom'; 
import { UserCircle, LogOut, Loader2, Menu, X } from 'lucide-react';

import Dashboard from './pages/Dashboard/Dashboard';
import LeadsBoard from './pages/Leads/LeadsBoard/LeadsBoard';
import MailingPage from './pages/Mail/MailingPage';
import ManagersPage from './pages/Managers/ManagersPage';
import TimerManager from './pages/TimerManager/TimerManager';
import TasksPage from './pages/Tasks/TasksPage';
import Login from './pages/Auth/Login';
import SystemPage from './pages/System/SystemPage';

import api from './services/api'; 
import './App.css';

// --- ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ---
const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
};

const RoleRoute = ({ children, allow }) => {
  const user = getStoredUser();
  if (!user || !allow.includes(user.role)) {
    return <Navigate to={user?.role === 'manager' ? '/timer' : '/dashboard'} replace />;
  }
  return children;
};

const NavLink = ({ to, children, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link 
      to={to} 
      onClick={onClick}
      className={`relative block py-3 md:py-1 text-xs font-black uppercase tracking-[0.2em] transition-all duration-300 min-h-[44px] md:min-h-0 flex items-center ${
        isActive ? 'text-white' : 'text-slate-500 hover:text-slate-300'
      }`}
    >
      {children}
      {isActive && (
        <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.8)]" />
      )}
    </Link>
  );
};

// --- КОМПОНЕНТ НАВИГАЦИИ С ЛОАДЕРОМ ВЫХОДА ---
const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate(); 
  const user = getStoredUser();
  const isManager = user?.role === 'manager';
  
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (location.pathname === '/login') return null;

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await api.post('/logout'); 
    } catch (err) {
      console.error("Ошибка при выходе:", err);
    } finally {
      localStorage.clear(); 
      setTimeout(() => {
        window.location.href = '/login'; 
      }, 500);
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const navLinks = (
    <>
      {!isManager && <NavLink to="/dashboard" onClick={closeMobileMenu}>Дашборд</NavLink>}
      <NavLink to="/leads" onClick={closeMobileMenu}>Сделки</NavLink>
      <NavLink to="/mailing" onClick={closeMobileMenu}>Рассылки</NavLink>
      {!isManager && <NavLink to="/system" onClick={closeMobileMenu}>Аудит</NavLink>}
      <NavLink to="/tasks" onClick={closeMobileMenu}>Задачи</NavLink>
      {isManager ? (
        <NavLink to="/timer" onClick={closeMobileMenu}>Таймер</NavLink>
      ) : (
        <NavLink to="/managers" onClick={closeMobileMenu}>Команда</NavLink>
      )}
    </>
  );

  return (
    <>
      <nav className="bg-[#0f172a]/80 backdrop-blur-xl border-b border-white/5 px-4 sm:px-6 md:px-8 py-4 md:py-5 sticky top-0 z-[50]">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          
          <div className="flex items-center gap-2 sm:gap-4 group cursor-pointer min-w-0" onClick={() => { navigate(isManager ? '/timer' : '/dashboard'); closeMobileMenu(); }}>
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 bg-indigo-600 blur-lg opacity-20 group-hover:opacity-40 transition-opacity" />
              <div className="relative w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white shadow-2xl">
                <span className="font-black text-lg sm:text-xl italic">Ү</span>
              </div>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-black text-base sm:text-lg tracking-tighter text-white leading-none truncate">ҮРКЕР</span>
              <span className="text-[9px] sm:text-[10px] font-bold text-indigo-400 tracking-[0.15em] sm:tracking-[0.2em] leading-tight">CRM</span>
            </div>
          </div>
          
          {/* Десктоп: ссылки */}
          <div className="hidden md:flex gap-8 lg:gap-10">
            {navLinks}
          </div>

          {/* Десктоп: профиль и выход */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6 pl-4 lg:pl-6 border-l border-white/5">
            <div className="flex items-center gap-2 lg:gap-3">
              <div className="text-right hidden lg:block">
                <p className="text-[11px] font-black text-white leading-none truncate max-w-[120px]">
                  {user?.name || 'Пользователь'}
                </p>
                <p className="text-[9px] font-bold text-emerald-500 tracking-tighter uppercase">Online</p>
              </div>
              <div className="w-9 h-9 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-indigo-400 flex-shrink-0">
                <UserCircle size={20} />
              </div>
            </div>
            <button 
              onClick={handleLogout}
              disabled={isLoggingOut}
              className={`group flex items-center gap-2 px-3 lg:px-4 py-2 rounded-xl transition-all min-h-[44px] min-w-[44px] justify-center ${
                isLoggingOut 
                ? 'bg-rose-500/20 text-rose-500 cursor-not-allowed' 
                : 'hover:bg-rose-500/10 text-slate-500 hover:text-rose-500'
              }`}
            >
              {isLoggingOut ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <LogOut size={18} />
              )}
              <span className="text-[10px] font-black uppercase tracking-widest hidden lg:inline">
                {isLoggingOut ? 'Выход...' : ''}
              </span>
            </button>
          </div>

          {/* Мобильный: кнопка меню */}
          <div className="flex md:hidden items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="p-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:bg-indigo-500/20 transition-all min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Открыть меню"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </nav>

      {/* Мобильное меню (оверлей) */}
      <div
        className={`fixed inset-0 z-[100] md:hidden transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        aria-hidden={!mobileMenuOpen}
      >
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={closeMobileMenu}
        />
        <div className={`absolute top-0 right-0 w-full max-w-[320px] sm:max-w-[360px] h-full bg-[#0f172a] border-l border-white/10 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <span className="text-xs font-black uppercase tracking-widest text-slate-500">Меню</span>
            <button
              type="button"
              onClick={closeMobileMenu}
              className="p-2.5 rounded-xl bg-white/5 text-slate-400 hover:text-white hover:bg-rose-500/20 transition-all min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Закрыть меню"
            >
              <X size={22} />
            </button>
          </div>
          <div className="flex flex-col gap-1 p-4 overflow-y-auto">
            {navLinks}
          </div>
          <div className="mt-auto p-4 border-t border-white/5 space-y-2">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
              <div className="w-10 h-10 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-indigo-400 flex-shrink-0">
                <UserCircle size={22} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-black text-white truncate">{user?.name || 'Пользователь'}</p>
                <p className="text-[10px] font-bold text-emerald-500 uppercase">Online</p>
              </div>
            </div>
            <button 
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-black text-[10px] uppercase tracking-widest min-h-[48px] transition-all disabled:opacity-50 bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20"
            >
              {isLoggingOut ? <Loader2 size={18} className="animate-spin" /> : <LogOut size={18} />}
              {isLoggingOut ? 'Выход...' : 'Выход'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// --- ГЛАВНЫЙ КОМПОНЕНТ ---
function App() {
  useEffect(() => {
    const syncLogout = (e) => {
      if (e.key === 'token' && !e.newValue) {
        window.location.href = '/login';
      }
    };
    window.addEventListener('storage', syncLogout);
    return () => window.removeEventListener('storage', syncLogout);
  }, []);

  const user = getStoredUser();

  return (
    <Router>
      <div className="min-h-screen bg-[#0f172a] selection:bg-indigo-500/30">
        <Navigation />

        <main className="w-full max-w-full">
          <Routes>
            <Route path="/login" element={
              localStorage.getItem('token') ? <Navigate to="/" replace /> : <Login />
            } />

            <Route path="/dashboard" element={
              <PrivateRoute>
                <RoleRoute allow={['admin']}><Dashboard /></RoleRoute>
              </PrivateRoute>
            } />

            <Route path="/leads" element={<PrivateRoute><LeadsBoard /></PrivateRoute>} />
            <Route path="/mailing" element={<PrivateRoute><MailingPage /></PrivateRoute>} />
            <Route path="/system" element={<PrivateRoute><RoleRoute allow={['admin']}><SystemPage /></RoleRoute></PrivateRoute>} />
            <Route path="/tasks" element={<PrivateRoute><TasksPage /></PrivateRoute>} />
            <Route path="/timer" element={<PrivateRoute><TimerManager /></PrivateRoute>} />
            
            <Route path="/managers" element={
              <PrivateRoute>
                <RoleRoute allow={['admin']}><ManagersPage /></RoleRoute>
              </PrivateRoute>
            } />

            <Route path="/" element={
              <PrivateRoute>
                <Navigate to={user?.role === 'manager' ? '/timer' : '/dashboard'} replace />
              </PrivateRoute>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
